import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldAlert, RefreshCw } from 'lucide-react';
import AdminStatusStrip from '../components/AdminStatusStrip';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_OPTIONS = ['all', 'critical', 'high', 'medium', 'low', 'info'];
const EVENT_TYPE_OPTIONS = [
  'all',
  'admin_login_success',
  'admin_login_failure',
  'admin_route_denied',
  'rate_limit_triggered',
  'manual_worker_run',
  'source_registry_changed',
  'mark_reviewed',
  'ssrf_url_blocked',
  'file_upload_rejected',
  'oversized_payload_rejected',
  'llm_forbidden_phrase_fallback',
  'llm_fallback_other',
  'resend_send_failure',
  'secrets_exposure_attempt',
];
const HOURS_OPTIONS = [24, 72, 168, 720];

const SEVERITY_COLOR = {
  critical: '#FF5C5C',
  high: '#FF8C42',
  medium: '#F5C542',
  low: '#7DD3FC',
  info: '#9aa3ad',
};

const SecurityEvents = () => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [severity, setSeverity] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [routeFilter, setRouteFilter] = useState('');
  const [hours, setHours] = useState(168);
  const [q, setQ] = useState('');

  const params = useMemo(() => {
    const p = { hours, limit: 100 };
    if (severity !== 'all') p.severity = severity;
    if (eventType !== 'all') p.event_type = eventType;
    if (routeFilter.trim()) p.route = routeFilter.trim();
    if (q.trim()) p.q = q.trim();
    return p;
  }, [severity, eventType, routeFilter, hours, q]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/security/events`, {
        withCredentials: true,
        params,
      });
      setEvents(r.data?.events || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(e?.response?.status ? `HTTP ${e.response.status}` : 'Network error');
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="hussar-landing" data-testid="admin-security-events-page" style={{ minHeight: '100vh' }}>
      <div className="hussar-container" style={{ paddingTop: 32, paddingBottom: 80 }}>
        <AdminStatusStrip />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <ShieldAlert className="w-6 h-6" style={{ color: '#7DD3FC' }} />
          <h1
            style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
            data-testid="security-events-h1"
          >
            Security Events
          </h1>
        </div>
        <p style={{ marginTop: 8, opacity: 0.75, fontSize: 14, maxWidth: 760 }}>
          Audyt zdarzeń bezpieczeństwa: logowania, odmowy dostępu, rate-limity, SSRF, fallbacki LLM.
          IP są hashowane (SHA-256), nie przechowujemy surowych adresów, ciasteczek ani JWT.
        </p>

        {/* Filters */}
        <Card
          data-testid="security-events-filters"
          style={{
            padding: 16,
            marginTop: 18,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <div>
            <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 4 }}>
              Severity
            </label>
            <select
              data-testid="security-filter-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              style={{
                width: '100%', padding: 8, borderRadius: 6,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e5e7eb', fontSize: 13,
              }}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 4 }}>
              Event type
            </label>
            <select
              data-testid="security-filter-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              style={{
                width: '100%', padding: 8, borderRadius: 6,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e5e7eb', fontSize: 13,
              }}
            >
              {EVENT_TYPE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 4 }}>
              Route prefix
            </label>
            <Input
              data-testid="security-filter-route"
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value)}
              placeholder="/api/auth"
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 4 }}>
              Window (h)
            </label>
            <select
              data-testid="security-filter-hours"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              style={{
                width: '100%', padding: 8, borderRadius: 6,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e5e7eb', fontSize: 13,
              }}
            >
              {HOURS_OPTIONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 4 }}>
              Search
            </label>
            <Input
              data-testid="security-filter-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="event_type, route…"
              style={{ fontSize: 13 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              onClick={refresh}
              data-testid="security-refresh-btn"
              style={{ width: '100%' }}
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Odśwież
            </Button>
          </div>
        </Card>

        {/* Status */}
        <div style={{ marginTop: 14, fontSize: 13, opacity: 0.8 }}>
          {loading ? (
            <span data-testid="security-events-loading" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Loader2 className="w-3 h-3 animate-spin" /> Ładowanie…
            </span>
          ) : err ? (
            <span data-testid="security-events-error" style={{ color: '#FF5C5C' }}>
              Błąd: {err}
            </span>
          ) : (
            <span data-testid="security-events-total">Łącznie: {total} zdarzeń</span>
          )}
        </div>

        {/* Event cards */}
        <div data-testid="security-events-list" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {events.map((ev) => (
            <Card
              key={ev.event_id || ev.correlation_id}
              data-testid={`security-event-${ev.event_type}-${ev.event_id || ev.correlation_id}`}
              style={{
                padding: 14,
                borderLeft: `3px solid ${SEVERITY_COLOR[ev.severity] || '#6B7280'}`,
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: SEVERITY_COLOR[ev.severity] || '#6B7280',
                    }}
                  >
                    {ev.severity}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{ev.event_type}</span>
                </div>
                <span style={{ fontSize: 11, opacity: 0.65 }}>
                  {ev.created_at}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 8, fontSize: 12, opacity: 0.85 }}>
                <div><strong>route:</strong> <span style={{ opacity: 0.85 }}>{ev.route || '—'}</span></div>
                <div><strong>actor_role:</strong> <span style={{ opacity: 0.85 }}>{ev.actor_role || '—'}</span></div>
                <div><strong>actor_user_id:</strong> <span style={{ opacity: 0.85 }}>{ev.actor_user_id ? `${String(ev.actor_user_id).slice(0, 12)}…` : '—'}</span></div>
                <div><strong>ip_hash:</strong> <span style={{ opacity: 0.85, fontFamily: 'monospace', fontSize: 11 }}>{ev.ip_hash ? `${ev.ip_hash.slice(0, 16)}…` : '—'}</span></div>
              </div>
              {ev.metadata_safe && Object.keys(ev.metadata_safe).length > 0 && (
                <pre
                  style={{
                    marginTop: 8,
                    background: 'rgba(0,0,0,0.25)',
                    padding: 10,
                    borderRadius: 6,
                    fontSize: 11,
                    overflowX: 'auto',
                    opacity: 0.9,
                  }}
                >{JSON.stringify(ev.metadata_safe, null, 2)}</pre>
              )}
            </Card>
          ))}
          {!loading && events.length === 0 && (
            <Card data-testid="security-events-empty" style={{ padding: 20, opacity: 0.7 }}>
              Brak zdarzeń w wybranym oknie.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityEvents;
